import { useState } from 'react';
import { ArrowRight, Lock, ShieldCheck } from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Logo } from '../../components/Logo';

/** Platform-admin sign-in (admin.itcitysoft.com/login) — full-screen with 2FA hint. */
export function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [stay, setStay] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => navigate({ to: '/admin' }), 800);
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark flex flex-col">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Logo />
          <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Possignin <span className="text-zinc-400 dark:text-zinc-500 font-normal">Admin</span>
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400">
            <ShieldCheck className="w-3.5 h-3.5" />
            ITCity platform · staff only
          </span>
          <AuthThemeToggle />
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10">
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
              Sign in to admin
            </h1>
            <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
              Restricted to ITCity platform staff.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <Input
              label="Email"
              type="email"
              required
              placeholder="you@itcitysoft.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <div>
              <Input
                label="Password"
                type="password"
                required
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <div className="flex items-center justify-between mt-2">
                <label className="inline-flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300">
                  <input
                    type="checkbox"
                    checked={stay}
                    onChange={(e) => setStay(e.target.checked)}
                    className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  Stay signed in
                </label>
                <Link to="/forgot-password" className="text-xs font-medium text-brand dark:text-brand-dm hover:underline">
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              fullWidth
              loading={submitting}
              iconRight={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white/60 dark:bg-zinc-900/60 px-4 py-3 flex items-start gap-2.5 text-xs text-zinc-600 dark:text-zinc-300">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-brand dark:text-brand-dm flex-shrink-0" />
              <div>
                <div className="font-medium text-zinc-900 dark:text-zinc-100">Two-factor required</div>
                A 2FA prompt appears after password verification. Use your authenticator app or
                hardware key. Recovery codes work as a fallback.
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400 justify-center">
              <Lock className="w-3 h-3" />
              <span>Encrypted · audit-logged · IP-gated</span>
            </div>
          </form>
        </div>
      </main>

      <footer className="px-6 sm:px-10 py-6 text-xs text-zinc-500 dark:text-zinc-400 flex flex-col sm:flex-row items-center gap-2 sm:gap-4 justify-between">
        <span>© 2026 ITCity Ltd. · Sylhet, Bangladesh</span>
        <span className="flex items-center gap-3">
          <a href="https://status.itcitysoft.com" target="_blank" rel="noreferrer" className="hover:text-zinc-700 dark:hover:text-zinc-200 transition">Status</a>
          <a href="https://itcitysoft.com/security" target="_blank" rel="noreferrer" className="hover:text-zinc-700 dark:hover:text-zinc-200 transition">Security</a>
          <a href="mailto:support@itcitysoft.com" className="hover:text-zinc-700 dark:hover:text-zinc-200 transition">Help</a>
        </span>
      </footer>
    </div>
  );
}
